import React, { useContext, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Field, useField } from 'react-final-form';
import {
  Avatar,
  CheckBoxField as BaseCheckBoxField,
  FlexColumn,
  FlexSpace,
  ImageField,
  InputField,
  PhoneField,
  PlacesAutocomplete,
  TextAreaField as BaseTextAreaField,
  ExplanationTooltip,
  CheckBox,
  PremiumModal,
  FlexMiddle
} from '@ubeya/shared-web/components';
import { ReactComponent as BasePremiumStartIcon } from '@ubeya/shared-web/assets/lock.svg';
import { maxMedia, media } from '@ubeya/shared-web/style';
import { required } from '@ubeya/shared/utils/form';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import { useRecoilState } from 'recoil';
import { premiumModal } from '@ubeya/shared/atoms/shared';
import { ACCOUNT_PAYING_STATUSES } from 'crm/constants';

const SideBar = styled(FlexColumn)`
  align-items: center;
  flex: 1;

  ${({ $full }) =>
    $full
      ? css`
          padding: 2% 10%;
        `
      : css`
          padding-top: 34px;
        `}
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.white};
  ${media.tablet`
    border-right: 1px solid ${({ theme }) => theme.colors.grey2};
  `}
`;

const Wrapper = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;

  .wrapper-check-box {
    padding: 9px 0;
  }
`;

const PremiumStartIcon = styled(BasePremiumStartIcon)`
  margin-left: 5px;
  fill: ${({ theme }) => theme.colors.primary};
`;

const Container = styled(FlexSpace)`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 0 60px 44px 60px;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  ${maxMedia.tablet`
    flex-direction: column;
  `};
  ${maxMedia.mobile`
    padding: 0 10px 44px 10px;
  `};
`;

const TextAreaField = styled(BaseTextAreaField)`
  > textarea {
    min-height: 60px;
    max-height: 350px;
    padding-bottom: 30px;
  }
`;

const CheckBoxField = styled(BaseCheckBoxField)`
  input[type='checkbox'] + label {
    cursor: pointer;
  }
`;

const SettingsArea = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: -16px;
  margin-top: 12px;
`;

// indented so it reads as dependent on the self-service checkbox above it
const NestedCheckBoxField = styled(CheckBoxField)`
  margin-left: 25px;
`;

const StyledExplanationTooltip = styled.div`
  .explanation-tooltip-title-section {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const Column = styled.div`
  flex: 1;
  width: 100%;
  > div {
    width: 100%;
    padding-top: 20px;
  }
`;

const Profile = ({ clientId, isClientEditable }) => {
  const { t } = useContext(ConfigContext);

  const { config } = useAccount();
  const {
    data: { isUbeyaAdmin }
  } = useUser();
  const [premiumModalPayload, setPremiumModalPayload] = useRecoilState(premiumModal);

  const {
    input: { value: name }
  } = useField('name');
  const {
    input: { value: image }
  } = useField('image');

  const {
    input: { value: deductClientBreaktimesIsActive, onChange: setDeductClientBreaktimesIsActive }
  } = useField('deductClientBreaktimesIsActive');

  const {
    input: { value: deductPayrollBreaktimesIsActive, onChange: setDeductPayrollBreaktimesIsActive }
  } = useField('deductPayrollBreaktimesIsActive');

  const {
    input: { value: autoAvailabilityCheck }
  } = useField('autoAvailabilityCheck');

  const {
    input: { value: allowClientToSelectEmployees, onChange: setAllowClientToSelectEmployees }
  } = useField('allowClientToSelectEmployees');

  const showAllowClientToSelectEmployees = !!config.enableClientSelectEmployeesFeature && !!autoAvailabilityCheck;

  useEffect(() => {
    if (config.enableClientSelectEmployeesFeature && !autoAvailabilityCheck && allowClientToSelectEmployees) {
      setAllowClientToSelectEmployees(false);
    }
  }, [
    config.enableClientSelectEmployeesFeature,
    autoAvailabilityCheck,
    allowClientToSelectEmployees,
    setAllowClientToSelectEmployees
  ]);

  const disableEditClientDetails = !isClientEditable;
  const isAccountPaying = ACCOUNT_PAYING_STATUSES.includes(config.billingStatus);
  // super admins can manage self-service even on freemium accounts
  const canUseSelfService = isUbeyaAdmin || isAccountPaying;

  return (
    <>
      <Wrapper>
        <SideBar $full={!clientId}>
          {disableEditClientDetails ? (
            <Avatar firstName={name} image={image} size="large" />
          ) : (
            <ImageField name="logo" />
          )}

          <Container>
            <Column>
              <InputField
                name="name"
                title={t('name')}
                validate={required}
                required
                autoFocus
                isDisabled={disableEditClientDetails}
              />
              <Field
                name="location"
                render={({ input, meta }) => (
                  <PlacesAutocomplete
                    title={t('address')}
                    {...input}
                    required
                    value={input.value?.address || ''}
                    onChange={(address, location) => input.onChange({ ...input.value, address, name, ...location })}
                    error={meta.touched && meta.error}
                    clearable
                  />
                )}
              />
              <PhoneField name="companyLine" title={t('companyLine')} type="phone" />
              <TextAreaField
                name="supplierNotes"
                title={<ExplanationTooltip title={t('notes')} body={t('clientNotesTooltip')} />}
              />
              <TextAreaField
                name="defaultComment"
                title={<ExplanationTooltip title={t('clientDefaultComment')} body={t('clientGeneralCommentTooltip')} />}
                maxLength={5000}
              />
            </Column>
            <Column>
              <SettingsArea>
                <FlexMiddle
                  onClick={
                    !canUseSelfService
                      ? () => setPremiumModalPayload({ featureType: 'selfService', source: 'selfService' })
                      : undefined
                  }>
                  <CheckBoxField
                    disabled={!canUseSelfService}
                    name="autoAvailabilityCheck"
                    title={
                      <StyledExplanationTooltip>
                        <ExplanationTooltip title={t('autoAvailabilityCheck')} body={t('crmSelfServiceModeTooltip')} />
                      </StyledExplanationTooltip>
                    }
                  />
                  {!isAccountPaying && <PremiumStartIcon />}
                </FlexMiddle>

                {showAllowClientToSelectEmployees && (
                  <NestedCheckBoxField
                    name="allowClientToSelectEmployees"
                    title={
                      <StyledExplanationTooltip>
                        <ExplanationTooltip
                          title={t('allowClientToSelectEmployees')}
                          body={t('allowClientToSelectEmployeesExplanation')}
                        />
                      </StyledExplanationTooltip>
                    }
                  />
                )}

                <CheckBoxField name="isSharePhoneNumbers" title={t('isSharePhoneNumbers')} />
                <CheckBoxField name="isShareSchedulingNotes" title={t('isShareSchedulingNotes')} />
                <CheckBoxField name="isShowEmployeeCompliance" title={t('isShowEmployeeCompliance')} />
                <CheckBoxField
                  name="allowClientToBlockEmployees"
                  title={
                    <StyledExplanationTooltip>
                      <ExplanationTooltip
                        title={t('allowClientToBlockEmployees')}
                        body={t('allowClientsToBlockEmployeesTooltip')}
                      />
                    </StyledExplanationTooltip>
                  }
                />
                <CheckBox
                  className="wrapper-check-box"
                  onChange={setDeductClientBreaktimesIsActive}
                  name="deductClientBreaktimesIsActive"
                  value={deductClientBreaktimesIsActive}
                  label={t('breaksArePaidToggleCrm')}
                />
                <CheckBox
                  className="wrapper-check-box"
                  onChange={setDeductPayrollBreaktimesIsActive}
                  name="deductPayrollBreaktimesIsActive"
                  value={deductPayrollBreaktimesIsActive}
                  label={t('alsoDeductPayrollBreaks')}
                />
              </SettingsArea>
            </Column>
          </Container>
        </SideBar>
      </Wrapper>
      {!!premiumModalPayload && <PremiumModal />}
    </>
  );
};

export default Profile;
